import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { constants } from '../constants';
import { UserModel } from '../models/user.model';
import { catchError, timeout } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ErrorsService } from './errors.service';
import { AlertController, ModalController, NavController } from '@ionic/angular';

enum ResponseType
{
	blob = 'blob',
	json = 'json',
}

@Injectable({
	providedIn: 'root',
})
export class ApiService
{
	private authUser: UserModel;
	private apiUrl: string;

	constructor(
		private http: HttpClient,
		private errorService: ErrorsService,
		private navController: NavController,
		private modalController: ModalController,
		private alertController: AlertController
	)
	{
		this.setApiUrl(window.localStorage.getItem(constants.API_URL_NAME));
	}

	public setAuthUser(authUser: UserModel)
	{
		this.authUser = authUser;
	}

	public setApiUrl(apiUrl: string)
	{
		this.apiUrl = apiUrl;
	}

	public getApiUrl(): string
	{
		return this.apiUrl;
	}

	public get(
		url: string,
		headers?: HttpHeaders,
		params?: HttpParams,
		withAuthToken: boolean = false,
		requestTimeout = constants.GLOBAL_APP_TIMEOUT,
		useApiRoute = true,
		responseTypeObj?: 'json'
	)
	{
		return this.http
			.get(this.apiAuthUrl(url, withAuthToken, useApiRoute), {
				headers,
				params,
				responseType: responseTypeObj,
			})
			.pipe(
				timeout(requestTimeout),
				catchError((error: any) =>
				{
					// Handle the error here
					if (error.status === 403)
					{
						console.error('ApiService 403 Forbidden Error:', error);
						// Handle the 403 error, e.g., return an empty response or throw a custom error
						this.doLogout();
						// Alternatively, you can throw a custom error message
						// return throwError('Access Denied: You do not have permission to access this resource.');
					} else
					{
						// Handle other errors or rethrow them
						return throwError(error);
					}
				})
			);
	}

	doLogout()
	{
		try {
			this.modalController.dismiss();
			this.alertController.dismiss();
		} catch (error) { }
		this.navController.navigateRoot('/login');
	}

	public post<T>(
		url: string,
		headers?: HttpHeaders,
		params?: HttpParams,
		withAuthToken: boolean = false,
		requestTimeout = constants.GLOBAL_APP_TIMEOUT,
		hasGetParams = false
	)
	{
		if (withAuthToken)
		{
			params = this.apiPutPostParams(params);
		}

		return this.http
			.post<T>(
				this.apiAuthUrl(url, withAuthToken, true, hasGetParams),
				params,
				{ headers }
			)
			.pipe(timeout(requestTimeout));
	}

	public put(
		url: string,
		headers?: HttpHeaders,
		params?: HttpParams,
		withAuthToken: boolean = false,
		requestTimeout = constants.GLOBAL_APP_TIMEOUT
	)
	{
		if (withAuthToken)
		{
			params = this.apiPutPostParams(params);
		}

		return this.http
			.put(this.apiAuthUrl(url, withAuthToken), params, { headers })
			.pipe(timeout(requestTimeout));
	}

	public delete(
		url: string,
		headers?: HttpHeaders,
		params?: HttpParams,
		withAuthToken: boolean = false,
		requestTimeout = constants.GLOBAL_APP_TIMEOUT
	)
	{
		return this.http
			.delete(this.apiAuthUrl(url, withAuthToken), { headers, params })
			.pipe(timeout(requestTimeout));
	}

	private apiAuthUrl(
		url: string,
		withAuthToken: boolean = false,
		useApiRoute = true,
		hasAuthParams = false
	)
	{
		const apiUrl = this.apiUrl + (useApiRoute ? '/api/v1/' : '') + url;

		if (withAuthToken && this.authUser)
		{
			return (
				apiUrl +
				(hasAuthParams ? '&' : '?') +
				'username=' +
				(this.authUser.username ?? '') +
				'&token=' +
				(this.authUser.token ?? '') +
				'&location=' +
				(this.authUser.location ?? '') +
				'&app_use=' +
				(this.authUser.appUse ?? '')
			);
		}
		return apiUrl;
	}

	private apiPutPostParams(params?: HttpParams): HttpParams
	{
		if (!params)
		{
			params = new HttpParams();
		}

		params = params.set('app_use', this.authUser.appUse);
		params = params.set('location', this.authUser.location);
		params = params.set('token', this.authUser.token);
		params = params.set('username', this.authUser.username);

		return params;
	}
}
